import { useCallback } from "react";

import { Action, ActionType, Marker, MarkerType, StencilType } from "../../types";
import { clearClassicEndPoint, frameStencilController, getPlayerBallStencil, getStencilById } from "../../utils";
import { UpdateFrameStencils } from "./use-update-frame-stencils";

/** ================================
 * Get Updated Marker Position
 ================================ */
/** helper function to calculate the updated marker object with new x and y values */
interface GetUpdatedMarkerPositionProps {
  marker: Marker;
  deltaX: number;
  deltaY: number;
}
const getUpdatedMarkerPosition = ({ marker, deltaX, deltaY }: GetUpdatedMarkerPositionProps): Marker => {
  const { pos } = marker;
  switch (marker.markerType) {
    // for these marker types, adjust position by delta position
    case MarkerType.PLAYER:
    case MarkerType.BALL:
    case MarkerType.MISC:
    case MarkerType.TEXT:
    case MarkerType.SHADING_RECTANGLE:
    case MarkerType.SHADING_OVAL:
      return { ...marker, pos: { x: pos.x + deltaX, y: pos.y + deltaY } };

    // for shading trapezoid, adjust position and shadeVertices by delta position
    case MarkerType.SHADING_TRAPEZOID:
      return {
        ...marker,
        pos: { x: pos.x + deltaX, y: pos.y + deltaY },
        shadeVertices: marker.shadeVertices.map((vertex) => ({ x: vertex.x + deltaX, y: vertex.y + deltaY })),
      };
  }
};

/** ================================
 * Get Updated Action Position
 ================================ */
/** helper function to calculate the updated action object with new x and y values */
interface GetUpdatedActionPositionProps {
  action: Action;
  deltaX: number;
  deltaY: number;
}
const getUpdatedActionPosition = ({ action, deltaX, deltaY }: GetUpdatedActionPositionProps): Action => {
  const { pos, endPos } = action;

  // calculate the action's new start and end positions
  const deltaStartEndPos = {
    pos: { x: pos.x + deltaX, y: pos.y + deltaY },
    endPos: { x: endPos.x + deltaX, y: endPos.y + deltaY },
  };

  return {
    ...action,
    ...deltaStartEndPos,
    midPoints: action.midPoints.map((point) => ({ ...point, x: point.x + deltaX, y: point.y + deltaY })),
  };
};

/** ================================
 * Update Stencil Position Props
 ================================ */
export interface UpdateStencilPositionProps {
  id: string;
  deltaX: number;
  deltaY: number;
}
export type UpdateStencilPositionFunc = (props: UpdateStencilPositionProps) => void;

export const useUpdateStencilPosition = (updateFrameStencils: UpdateFrameStencils): UpdateStencilPositionFunc => {
  return useCallback(
    ({ id, deltaX, deltaY }) => {
      // frame stencils set state
      updateFrameStencils((prevState) => {
        if (!prevState) return prevState;
        // find the stencil by its id
        const { stencil } = getStencilById({ stencils: prevState || [], id });
        if (!stencil) return prevState;

        const { stencilType } = stencil;
        switch (stencilType) {
          /** ======== Marker ======== */
          case StencilType.MARKER: {
            // get marker's new position
            const updatedMarker = getUpdatedMarkerPosition({ marker: stencil, deltaX, deltaY });

            const ballStencil = getPlayerBallStencil({ stencil: updatedMarker, frameStencils: prevState });

            const updatedBallStencil = ballStencil ? { ...ballStencil, pos: updatedMarker.pos } : null;

            const markersToUpdate = [updatedMarker, ...(updatedBallStencil ? [updatedBallStencil] : [])];
            return markersToUpdate.reduce(
              (acc, marker) => frameStencilController({ operation: "UPDATE", stencils: acc, stencil: marker }),
              prevState
            );
          }

          /** ======== Marker ======== */
          case StencilType.ACTION: {
            // get action's new position
            const updatedAction = getUpdatedActionPosition({ action: stencil, deltaX, deltaY });
            const isPlayerOrPossessionAction =
              updatedAction.actionType === ActionType.PLAYER || updatedAction.actionType === ActionType.POSSESSION;
            const isBallOrPossessionAction =
              updatedAction.actionType === ActionType.BALL || updatedAction.actionType === ActionType.POSSESSION;
            const updateActionNextCleared = {
              ...updatedAction,
              ...(isPlayerOrPossessionAction ? { nextPlayerAction: null } : {}),
              ...(isBallOrPossessionAction ? { nextBallAction: null } : {}),
            };

            return frameStencilController({
              operation: "UPDATE",
              stencils: prevState,
              stencil: clearClassicEndPoint(updateActionNextCleared),
            });
          }
        }
      });
    },
    [updateFrameStencils]
  );
};
